import { useState, useCallback, useEffect } from 'react';
import { toast } from 'sonner';

/**
 * Persists state in localStorage with automatic JSON serialization.
 * Includes error handling for quota exceeded and parse failures.
 *
 * @param key - localStorage key
 * @param defaultValue - initial value if key doesn't exist
 * @returns [value, setValue, removeValue] tuple
 */
export function useLocalStorage<T>(
  key: string,
  defaultValue: T
): [T, (value: T | ((prev: T) => T)) => void, () => void] {
  // Initialize from localStorage
  const [storedValue, setStoredValue] = useState<T>(() => {
    if (typeof window === 'undefined') {
      return defaultValue;
    }
    try {
      const item = window.localStorage.getItem(key);
      return item ? (JSON.parse(item) as T) : defaultValue;
    } catch (error) {
      console.warn(`useLocalStorage: Failed to parse ${key}`, error);
      return defaultValue;
    }
  });

  // Update localStorage when value changes
  const setValue = useCallback(
    (value: T | ((prev: T) => T)) => {
      try {
        const valueToStore =
          value instanceof Function ? value(storedValue) : value;
        setStoredValue(valueToStore);
        if (typeof window !== 'undefined') {
          window.localStorage.setItem(key, JSON.stringify(valueToStore));
        }
      } catch (error) {
        // Handle quota exceeded - clear old data and notify user
        if (
          error instanceof DOMException &&
          (error.name === 'QuotaExceededError' ||
            error.name === 'NS_ERROR_DOM_QUOTA_REACHED')
        ) {
          console.warn(`useLocalStorage: Quota exceeded for ${key}, clearing`);
          window.localStorage.removeItem(key);
          toast.warning('Preferences not saved: storage limit reached');
        } else {
          console.error(`useLocalStorage: Failed to set ${key}`, error);
        }
      }
    },
    [key, storedValue]
  );

  // Remove from localStorage
  const removeValue = useCallback(() => {
    try {
      setStoredValue(defaultValue);
      if (typeof window !== 'undefined') {
        window.localStorage.removeItem(key);
      }
    } catch (error) {
      console.error(`useLocalStorage: Failed to remove ${key}`, error);
    }
  }, [key, defaultValue]);

  // Sync across tabs
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === key && e.newValue !== null) {
        try {
          setStoredValue(JSON.parse(e.newValue) as T);
        } catch {
          setStoredValue(defaultValue);
        }
      } else if (e.key === key && e.newValue === null) {
        setStoredValue(defaultValue);
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [key, defaultValue]);

  return [storedValue, setValue, removeValue];
}

/**
 * Alert dismissal record with timestamp for auto-cleanup
 */
interface DismissalRecord {
  id: string;
  dismissedAt: number; // Unix timestamp
}

/**
 * Specialized hook for alert dismissals with auto-cleanup of old entries.
 * Cleans up dismissals older than maxAgeMs on mount.
 *
 * @param key - localStorage key
 * @param maxAgeMs - max age in milliseconds (default 24 hours)
 */
export function useAlertDismissals(
  key: string,
  maxAgeMs: number = 24 * 60 * 60 * 1000
): {
  dismissedIds: Set<string>;
  dismiss: (id: string) => void;
  isDismissed: (id: string) => boolean;
  clearAll: () => void;
} {
  const [records, setRecords, removeRecords] = useLocalStorage<
    DismissalRecord[]
  >(key, []);

  // Clean up old dismissals on mount
  useEffect(() => {
    const now = Date.now();
    const validRecords = records.filter(
      (r) => now - r.dismissedAt < maxAgeMs
    );
    if (validRecords.length !== records.length) {
      setRecords(validRecords);
    }
    // Only run on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const dismissedIds = new Set(records.map((r) => r.id));

  const dismiss = useCallback(
    (id: string) => {
      setRecords((prev) => {
        if (prev.some((r) => r.id === id)) {
          return prev;
        }
        return [...prev, { id, dismissedAt: Date.now() }];
      });
    },
    [setRecords]
  );

  const isDismissed = useCallback(
    (id: string) => dismissedIds.has(id),
    [dismissedIds]
  );

  const clearAll = useCallback(() => {
    removeRecords();
  }, [removeRecords]);

  return { dismissedIds, dismiss, isDismissed, clearAll };
}
